// DCVFD Finance Package — multi-sheet, branded .xlsx workbook builder.
//
// This is the packaged workbook the Board / finance staff download for the
// semi-monthly meetings. It pulls together every finance perspective the
// dashboard already surfaces (levy budgetary control, open POs, FY27 budget +
// bridge + initiatives, the 501(c)(4) operating book, and derived QBO metrics)
// into ONE branded file with a tab per perspective and a plain-English cover.
//
// Design rules (matching the dashboard's "never overstate spendable" discipline):
//   • Branding: every sheet gets the DCVFD/Ember logo, the org + package title,
//     the as-of date, FY scope, and a thin DCVFD-green (#163832) accent rule.
//   • The levy "Available" nuance is carried through verbatim: the by-object
//     table column is labelled "Budget Remaining (gross)" — NOT "Available" —
//     because the gross figure overstates what we can actually still spend. The
//     3-lens reconciliation ($158,751 → $47,225 → $24,913) sits up top.
//   • Numbers are formatted as currency ($#,##0), right-aligned; headers are
//     bold and frozen; borders are subtle (thin light-grey), never fat.
//   • NEVER fabricates: a missing endpoint renders "—" / "(syncing)" in that
//     sheet and the rest of the workbook still builds. Derived metrics show
//     "n/a" when a denominator is missing rather than inventing a value.
//
// exceljs is loaded LAZILY via dynamic import() so it lands in its own chunk and
// doesn't bloat the main bundle (the package is only built on button click).
//
// All inputs are the SAME data the pages already loaded via the finance hooks;
// the caller passes a `FinancePackageData` snapshot. The builder is pure data →
// workbook; the only side effects are the logo fetch and the Blob download.

import { fundForFy } from "./fiscal";
import type {
  FinanceSummary,
  FinanceAvailable,
  ByObjectRow,
  OpenPoRow,
  Fy27BuildResponse,
  Fy27BridgeResponse,
  Fy27V2Response,
  ProjectRow,
  NonprofitSummary,
  NonprofitIncomeBySource,
  NonprofitExpensesByCategory,
  NonprofitTimeseries,
} from "../hooks/useFinance";

// ── brand tokens ──────────────────────────────────────────────────────────────
const BRAND_GREEN = "FF163832"; // DCVFD dark green (ARGB) — accent rule + title
const BRAND_ACCENT = "FF2A7A6C"; // lighter accent for sub-rules / totals
const HEADER_FILL = "FF163832";
const HEADER_TEXT = "FFFFFFFF";
const ZEBRA_FILL = "FFF3F6F5"; // very faint green-grey zebra
const ROLLUP_FILL = "FFEAF5EA"; // light DCVFD green — committee/subtotal/rollup rows
const SUBTLE_BORDER = "FFE2E8E6";
const NOTE_FILL = "FFFFF7E6"; // soft amber for the important caveat note
const LOGO_PATH = "/brand/ember-light.png"; // 500×150 PNG (10:3)

// Board-standard workbook font. Aptos everywhere (headers, body, totals, cover)
// — applied via the `font()` helper so the name is never forgotten on a cell.
const FONT = "Aptos";

const USD_FMT = "$#,##0;[Red]-$#,##0";
// Cent-exact — the FY27 v2 open-PO carryover / ALL-IN figures tie to the cent.
const USD_CENTS_FMT = "$#,##0.00;[Red]-$#,##0.00";
const PCT_FMT = "0.0%";

// Chart brand palette (DCVFD green family) for the embedded canvas charts.
const CHART_GREEN = "#163832";
const CHART_GREEN_MID = "#2A7A6C";
const CHART_AMBER = "#B8860B";
const CHART_GRID = "#E2E8E6";
const CHART_TEXT = "#444444";

// exceljs Font type, narrowed to what we set. `font(spec)` stamps the Aptos
// name onto every font we apply so the whole workbook renders in one typeface.
type FontSpec = {
  name?: string;
  bold?: boolean;
  italic?: boolean;
  size?: number;
  color?: { argb: string };
};
function font(spec: Omit<FontSpec, "name"> = {}): FontSpec {
  return { name: FONT, ...spec };
}

// exceljs types come from its own bundled index.d.ts. We import the module type
// for annotations but load the value dynamically.
type ExcelJSModule = typeof import("exceljs");
type Workbook = InstanceType<ExcelJSModule["Workbook"]>;
type Worksheet = ReturnType<Workbook["addWorksheet"]>;

const num = (v: unknown, f = 0): number => {
  if (typeof v === "number") return Number.isFinite(v) ? v : f;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : f;
  }
  return f;
};

// ── offscreen-canvas charts ───────────────────────────────────────────────────
// exceljs has no native charting, so we render each chart to a PNG on an
// offscreen <canvas> (Canvas 2D — no extra dependency) and embed the image with
// workbook.addImage + worksheet.addImage. All of this is guarded by `document`
// availability so the builder still runs (sans charts) under SSR / the node
// test environment. A chart whose data is empty returns null and is skipped.

interface ChartSeries {
  label: string;
  color: string;
}
type ImageExt = { width: number; height: number };

const CHART_W = 900; // logical px (rendered @2x for crispness)
const CHART_H = 360;

function newCanvas(): HTMLCanvasElement | null {
  if (typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  if (typeof c.getContext !== "function") return null;
  return c;
}

/** Money tick label: $1.4M / $846K / $500. */
function tick(n: number): string {
  const a = Math.abs(n);
  if (a >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

function setupCtx(): {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
} | null {
  const canvas = newCanvas();
  if (!canvas) return null;
  const dpr = 2;
  canvas.width = CHART_W * dpr;
  canvas.height = CHART_H * dpr;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.scale(dpr, dpr);
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, CHART_W, CHART_H);
  ctx.textBaseline = "middle";
  return { canvas, ctx };
}

function canvasToBuffer(canvas: HTMLCanvasElement): ArrayBuffer | null {
  try {
    const dataUrl = canvas.toDataURL("image/png");
    const b64 = dataUrl.split(",")[1];
    if (!b64) return null;
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
  } catch {
    return null;
  }
}

/** Convert a chart PNG ArrayBuffer (from renderBarChart/renderLineChart) into a
 *  data URL for jsPDF's addImage — so the PDF uses the EXACT same canvas-rendered
 *  charts as the XLSX (and matches the dashboard palette). Returns null if no
 *  chart could be produced (e.g. no canvas / no data). */
export function chartPngDataUrl(buf: ArrayBuffer | null): string | null {
  if (!buf) return null;
  const bytes = new Uint8Array(buf);
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return `data:image/png;base64,${btoa(bin)}`;
}

/** Logical chart aspect ratio (CHART_W × CHART_H) so PDF callers can size the
 *  image without distortion. */
export const CHART_ASPECT = CHART_W / CHART_H;

/** Grouped/simple vertical bar chart → PNG ArrayBuffer (null if no canvas/data). */
export function renderBarChart(
  title: string,
  categories: string[],
  series: { color: string; values: number[] }[],
): ArrayBuffer | null {
  if (categories.length === 0) return null;
  const s = setupCtx();
  if (!s) return null;
  const { canvas, ctx } = s;

  const padL = 70;
  const padR = 24;
  const padT = 44;
  const padB = 78;
  const plotW = CHART_W - padL - padR;
  const plotH = CHART_H - padT - padB;

  // title
  ctx.fillStyle = CHART_GREEN;
  ctx.font = "bold 18px Aptos, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(title, padL, 22);

  const allVals = series.flatMap((x) => x.values);
  const max = Math.max(1, ...allVals);
  const niceMax = niceCeil(max);

  // y gridlines + labels
  ctx.textAlign = "right";
  ctx.font = "11px Aptos, sans-serif";
  const ticks = 4;
  for (let i = 0; i <= ticks; i++) {
    const v = (niceMax / ticks) * i;
    const y = padT + plotH - (v / niceMax) * plotH;
    ctx.strokeStyle = CHART_GRID;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padL, y);
    ctx.lineTo(padL + plotW, y);
    ctx.stroke();
    ctx.fillStyle = CHART_TEXT;
    ctx.fillText(tick(v), padL - 8, y);
  }

  // bars (grouped)
  const groupW = plotW / categories.length;
  const nSeries = series.length;
  const barGap = 6;
  const barW = Math.max(
    4,
    (groupW * 0.7 - barGap * (nSeries - 1)) / Math.max(1, nSeries),
  );
  categories.forEach((cat, ci) => {
    const gx = padL + ci * groupW + groupW * 0.15;
    series.forEach((ser, si) => {
      const v = ser.values[ci] ?? 0;
      const h = (Math.max(0, v) / niceMax) * plotH;
      const x = gx + si * (barW + barGap);
      const y = padT + plotH - h;
      ctx.fillStyle = ser.color;
      ctx.fillRect(x, y, barW, h);
    });
    // category label (rotated if many)
    ctx.save();
    ctx.fillStyle = CHART_TEXT;
    ctx.font = "11px Aptos, sans-serif";
    const cx = padL + ci * groupW + groupW / 2;
    if (categories.length > 6) {
      ctx.translate(cx, padT + plotH + 8);
      ctx.rotate(-Math.PI / 4);
      ctx.textAlign = "right";
      ctx.fillText(truncate(cat, 18), 0, 0);
    } else {
      ctx.textAlign = "center";
      ctx.fillText(truncate(cat, 16), cx, padT + plotH + 14);
    }
    ctx.restore();
  });

  return canvasToBuffer(canvas);
}

/** Multi-series line chart over time → PNG ArrayBuffer. */
export function renderLineChart(
  title: string,
  categories: string[],
  series: { label: string; color: string; values: number[] }[],
): ArrayBuffer | null {
  if (categories.length === 0) return null;
  const s = setupCtx();
  if (!s) return null;
  const { canvas, ctx } = s;

  const padL = 70;
  const padR = 24;
  const padT = 44;
  const padB = 64;
  const plotW = CHART_W - padL - padR;
  const plotH = CHART_H - padT - padB;

  ctx.fillStyle = CHART_GREEN;
  ctx.font = "bold 18px Aptos, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(title, padL, 22);

  const allVals = series.flatMap((x) => x.values);
  const max = Math.max(1, ...allVals, 0);
  const min = Math.min(0, ...allVals);
  const niceMax = niceCeil(max);
  const niceMin = min < 0 ? -niceCeil(-min) : 0;
  const range = niceMax - niceMin || 1;
  const yOf = (v: number) => padT + plotH - ((v - niceMin) / range) * plotH;

  // y gridlines
  ctx.textAlign = "right";
  ctx.font = "11px Aptos, sans-serif";
  const ticks = 4;
  for (let i = 0; i <= ticks; i++) {
    const v = niceMin + (range / ticks) * i;
    const y = yOf(v);
    ctx.strokeStyle = CHART_GRID;
    ctx.beginPath();
    ctx.moveTo(padL, y);
    ctx.lineTo(padL + plotW, y);
    ctx.stroke();
    ctx.fillStyle = CHART_TEXT;
    ctx.fillText(tick(v), padL - 8, y);
  }

  const n = categories.length;
  const xOf = (i: number) =>
    padL + (n === 1 ? plotW / 2 : (plotW / (n - 1)) * i);

  // lines
  series.forEach((ser) => {
    ctx.strokeStyle = ser.color;
    ctx.lineWidth = 2.25;
    ctx.beginPath();
    ser.values.forEach((v, i) => {
      const x = xOf(i);
      const y = yOf(v);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
  });

  // x labels (thin out)
  ctx.fillStyle = CHART_TEXT;
  ctx.font = "10px Aptos, sans-serif";
  ctx.textAlign = "center";
  const step = Math.ceil(n / 8);
  categories.forEach((cat, i) => {
    if (i % step !== 0 && i !== n - 1) return;
    ctx.fillText(truncate(cat, 10), xOf(i), padT + plotH + 14);
  });

  // legend
  legend(ctx, series, padL, CHART_H - 18);

  return canvasToBuffer(canvas);
}

function legend(
  ctx: CanvasRenderingContext2D,
  series: ChartSeries[],
  x: number,
  y: number,
) {
  ctx.font = "11px Aptos, sans-serif";
  ctx.textBaseline = "middle";
  let cx = x;
  for (const ser of series) {
    ctx.fillStyle = ser.color;
    ctx.fillRect(cx, y - 5, 12, 10);
    cx += 16;
    ctx.fillStyle = CHART_TEXT;
    ctx.textAlign = "left";
    ctx.fillText(ser.label, cx, y);
    cx += ctx.measureText(ser.label).width + 22;
  }
}

function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / mag;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * mag;
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
}

/** Embed a chart PNG (if rendering succeeded) onto a sheet at the given row,
 *  and return the row index AFTER the image (so content can flow below it).
 *  No-op (returns the same row) when `buf` is null — chart-less but resilient. */
function embedChart(
  wb: Workbook,
  ws: Worksheet,
  buf: ArrayBuffer | null,
  topRow: number,
  ext: ImageExt = { width: 560, height: 224 },
): number {
  if (!buf) return topRow;
  const id = wb.addImage({
    buffer: buf as unknown as Parameters<typeof wb.addImage>[0]["buffer"],
    extension: "png",
  });
  ws.addImage(id, {
    tl: { col: 0, row: topRow - 1 },
    ext,
    editAs: "oneCell",
  });
  // Reserve ~ext.height/18 rows so following content doesn't overlap the image.
  const rowsTall = Math.ceil(ext.height / 18) + 1;
  return topRow + rowsTall;
}

// ── the snapshot the page hands us ────────────────────────────────────────────
// Every field is optional / nullable; a null source degrades that sheet to a
// "(syncing — no data yet)" note instead of crashing the export.
export interface FinancePackageData {
  asOf: string | null;
  curFy: string;
  nextFy: string;
  /** True when curFy is a PRIOR fiscal year than the one we're in now (the
   *  county hasn't loaded the current FY yet) — so the position sections are
   *  labeled "(prior year)" and not read as today's position. */
  curFyIsPrior?: boolean;
  summary: FinanceSummary | null;
  available: FinanceAvailable | null;
  byObject: ByObjectRow[] | null;
  openPos: OpenPoRow[] | null;
  fy27Build: Fy27BuildResponse | null;
  fy27Bridge: Fy27BridgeResponse | null;
  fy27V2: Fy27V2Response | null;
  projects: ProjectRow[] | null;
  nonprofit: NonprofitSummary | null;
  npIncome: NonprofitIncomeBySource | null;
  npExpenses: NonprofitExpensesByCategory | null;
  npTimeseries: NonprofitTimeseries | null;
  preparedBy?: string | null;
}

// One row in the Cover read-me table describing a tab.
interface TabDoc {
  tab: string;
  contains: string;
  howToRead: string;
}

// ── small worksheet helpers ───────────────────────────────────────────────────

/** Stamp the branded header block (logo + titles + accent rule) onto a sheet and
 *  return the 1-based row index where content should start. `imageId` is the
 *  workbook-level image id (or null when the logo failed to load). */
function brandHeader(
  ws: Worksheet,
  imageId: number | null,
  subtitle: string,
  data: FinancePackageData,
  lastCol: number,
): number {
  // ── Clean branded band ──
  // Row 1: tall band. Logo floats over the left (cols A–B, merged behind it so
  // it sits cleanly). The package title sits in the middle, and the as-of/FY/
  // fund meta is RIGHT-ALIGNED in the rightmost columns — no awkward gaps.
  const bandRow = ws.getRow(1);
  bandRow.height = 50;
  // Faint band fill across the whole width so the header reads as one block.
  for (let c = 1; c <= lastCol; c++) {
    bandRow.getCell(c).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF6FAF8" },
    };
    bandRow.getCell(c).alignment = { vertical: "middle" };
  }

  const asOf = data.asOf ? `as of ${data.asOf}` : "as-of date pending sync";
  const metaText = `${data.curFy}${data.curFyIsPrior ? " (prior yr)" : ""} position  ·  ${data.nextFy} budget  ·  fund ${fundForFy(data.curFy)}  ·  ${asOf}`;

  if (lastCol <= 4) {
    // NARROW sheets (≤4 cols: Cover, Bridge, Non-Profit, QBO Metrics,
    // Compliance) can't fit three disjoint header zones — the old logo/title/
    // meta merges OVERLAPPED and exceljs throws "Cannot merge already merged
    // cells", blanking the whole sheet (Kelly's empty Cover + last-three-tabs
    // bug). Use ONE full-row merge with stacked title + meta; the logo floats
    // on top (addImage needs no merge). One merge → no overlap is possible.
    ws.mergeCells(1, 1, 1, lastCol);
    if (imageId != null) {
      ws.addImage(imageId, {
        tl: { col: 0.1, row: 0.1 },
        ext: { width: 150, height: 45 },
        editAs: "oneCell",
      });
    }
    const tc = ws.getCell(1, 1);
    tc.value = {
      richText: [
        {
          text: "Dale City Volunteer Fire Department\n",
          font: {
            name: FONT,
            bold: true,
            size: 13,
            color: { argb: BRAND_GREEN },
          },
        },
        {
          text: `Finance Package — ${subtitle}\n`,
          font: {
            name: FONT,
            bold: true,
            size: 10,
            color: { argb: "FF333333" },
          },
        },
        {
          text: metaText,
          font: { name: FONT, size: 9, color: { argb: "FF666666" } },
        },
      ],
    };
    tc.alignment = {
      vertical: "middle",
      wrapText: true,
      horizontal: "left",
      indent: imageId != null ? 20 : 1,
    };
  } else {
    // WIDE sheets: three DISJOINT zones, left→right — logo | title | meta — so
    // no two merges ever overlap.
    if (imageId != null) {
      ws.mergeCells(1, 1, 1, 2); // logo over A1:B1
      ws.addImage(imageId, {
        tl: { col: 0.1, row: 0.1 },
        ext: { width: 150, height: 45 },
        editAs: "oneCell",
      });
    }
    const titleStart = 3;
    const titleEnd = Math.max(titleStart, lastCol - 2);
    if (titleEnd > titleStart) ws.mergeCells(1, titleStart, 1, titleEnd);
    const titleCell = ws.getCell(1, titleStart);
    titleCell.value = {
      richText: [
        {
          text: "Dale City Volunteer Fire Department\n",
          font: {
            name: FONT,
            bold: true,
            size: 13,
            color: { argb: BRAND_GREEN },
          },
        },
        {
          text: `Finance Package — ${subtitle}`,
          font: {
            name: FONT,
            bold: true,
            size: 10,
            color: { argb: "FF333333" },
          },
        },
      ],
    };
    titleCell.alignment = {
      vertical: "middle",
      wrapText: true,
      horizontal: "left",
    };

    const metaStart = lastCol - 1; // last two columns, disjoint from the title
    ws.mergeCells(1, metaStart, 1, lastCol);
    const metaCell = ws.getCell(1, metaStart);
    metaCell.value = `${data.curFy}${data.curFyIsPrior ? " (prior yr)" : ""} position  ·  ${data.nextFy} budget\nfund ${fundForFy(data.curFy)}  ·  ${asOf}`;
    metaCell.font = font({ size: 9, color: { argb: "FF666666" } });
    metaCell.alignment = {
      vertical: "middle",
      horizontal: "right",
      wrapText: true,
    };
  }

  // Thin DCVFD-green accent rule across the full width (row 2).
  const ruleRow = ws.getRow(2);
  ruleRow.height = 4;
  for (let c = 1; c <= lastCol; c++) {
    ruleRow.getCell(c).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: BRAND_GREEN },
    };
  }
  // blank spacer row
  ws.getRow(3).height = 6;
  return 4; // content starts at row 4
}

/** Apply the standard bold header-row styling to a single row of column titles. */
function styleHeaderRow(ws: Worksheet, rowIdx: number, lastCol: number) {
  const row = ws.getRow(rowIdx);
  for (let c = 1; c <= lastCol; c++) {
    const cell = row.getCell(c);
    cell.font = font({ bold: true, size: 10, color: { argb: HEADER_TEXT } });
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: HEADER_FILL },
    };
    cell.alignment = { vertical: "middle", wrapText: true };
    cell.border = {
      bottom: { style: "thin", color: { argb: BRAND_ACCENT } },
    };
  }
  row.height = 26;
}

/** Subtle thin border on every cell in a data range, plus optional zebra fill.
 *  Also pins vertical-middle alignment so wrapped/tall cells are never
 *  bottom-stuck (preserving any horizontal alignment already set). */
function styleDataRow(
  ws: Worksheet,
  rowIdx: number,
  lastCol: number,
  zebra: boolean,
) {
  const row = ws.getRow(rowIdx);
  for (let c = 1; c <= lastCol; c++) {
    const cell = row.getCell(c);
    cell.border = {
      bottom: { style: "thin", color: { argb: SUBTLE_BORDER } },
    };
    cell.alignment = { ...(cell.alignment ?? {}), vertical: "middle" };
    if (!cell.font) cell.font = font();
    if (zebra) {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: ZEBRA_FILL },
      };
    }
  }
}

/** Light-green rollup/subtotal styling: a bold, light-DCVFD-green band across a
 *  row so committee headings and subtotal/rollup rows stand out from line items. */
function styleRollupRow(ws: Worksheet, rowIdx: number, lastCol: number) {
  const row = ws.getRow(rowIdx);
  for (let c = 1; c <= lastCol; c++) {
    const cell = row.getCell(c);
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: ROLLUP_FILL },
    };
    cell.alignment = { ...(cell.alignment ?? {}), vertical: "middle" };
    cell.border = {
      top: { style: "thin", color: { argb: BRAND_ACCENT } },
      bottom: { style: "thin", color: { argb: BRAND_ACCENT } },
    };
    const f = (cell.font as FontSpec) ?? {};
    cell.font = font({ ...f, bold: true });
  }
}

/** A "this sheet is syncing / has no data yet" placeholder line. */
function syncingNote(ws: Worksheet, rowIdx: number, what: string) {
  const cell = ws.getCell(`A${rowIdx}`);
  cell.value = `${what} is not available yet (source syncing). Re-export once the feed has refreshed.`;
  cell.font = font({ italic: true, color: { argb: "FF999999" }, size: 10 });
  cell.alignment = { vertical: "middle" };
}

/** A bold section caption row. */
function sectionTitle(ws: Worksheet, rowIdx: number, text: string) {
  const cell = ws.getCell(`A${rowIdx}`);
  cell.value = text;
  cell.font = font({ bold: true, size: 11, color: { argb: BRAND_GREEN } });
  cell.alignment = { vertical: "middle" };
}

/** A wrapped explanatory note row spanning A..lastCol on an amber background. */
function calloutNote(
  ws: Worksheet,
  rowIdx: number,
  lastCol: number,
  text: string,
) {
  ws.mergeCells(rowIdx, 1, rowIdx, lastCol);
  const cell = ws.getCell(rowIdx, 1);
  cell.value = text;
  cell.font = font({ size: 10, color: { argb: "FF7A5800" }, bold: true });
  cell.alignment = { wrapText: true, vertical: "middle" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: NOTE_FILL },
  };
  ws.getRow(rowIdx).height = 34;
}

// ── per-sheet builders ────────────────────────────────────────────────────────

function buildCover(
  ws: Worksheet,
  imageId: number | null,
  data: FinancePackageData,
  tabs: TabDoc[],
) {
  const LAST = 3;
  ws.columns = [{ width: 24 }, { width: 60 }, { width: 60 }];
  let r = brandHeader(ws, imageId, "Cover / Read Me", data, LAST);

  sectionTitle(ws, r, "DCVFD Finance Package");
  r += 1;
  const meta: [string, string][] = [
    ["Organization", "Dale City Volunteer Fire Department (DCVFD)"],
    ["Report", "Board / Finance semi-monthly Finance Package"],
    ["As-of date", data.asOf ?? "(pending data sync)"],
    [
      "Fiscal scope",
      `${data.curFy}${data.curFyIsPrior ? " (prior yr)" : ""} levy position · ${data.nextFy} budget · fund ${fundForFy(data.curFy)}`,
    ],
    [
      "Prepared by",
      data.preparedBy?.trim() || "DCVFD Finance (Ember dashboard)",
    ],
    ["Generated", new Date().toLocaleString("en-US")],
  ];
  for (const [k, v] of meta) {
    ws.getCell(`A${r}`).value = k;
    ws.getCell(`A${r}`).font = font({ bold: true, size: 10 });
    ws.mergeCells(r, 2, r, 3);
    ws.getCell(`B${r}`).value = v;
    ws.getCell(`B${r}`).alignment = { wrapText: true, vertical: "middle" };
    r += 1;
  }

  r += 1;
  calloutNote(
    ws,
    r,
    LAST,
    'Cardinal rule for the levy: "Budget Remaining (gross)" is NOT the same as ' +
      "what we can spend. The Funds Available (budgetary control) figure on the " +
      "Levy Position tab is the real spendable number — it nets in budgeted-but-" +
      "uncollected recovery revenue. Always quote Funds Available, not gross remaining.",
  );
  r += 2;

  sectionTitle(ws, r, "What each tab contains and how to read it");
  r += 1;

  ws.getCell(`A${r}`).value = "Tab";
  ws.getCell(`B${r}`).value = "What it contains";
  ws.getCell(`C${r}`).value = "How to read it";
  styleHeaderRow(ws, r, LAST);
  r += 1;

  tabs.forEach((t, i) => {
    ws.getCell(`A${r}`).value = t.tab;
    ws.getCell(`A${r}`).font = font({ bold: true, size: 10 });
    ws.getCell(`B${r}`).value = t.contains;
    ws.getCell(`C${r}`).value = t.howToRead;
    for (const col of ["A", "B", "C"]) {
      ws.getCell(`${col}${r}`).alignment = {
        wrapText: true,
        vertical: "middle",
      };
    }
    styleDataRow(ws, r, LAST, i % 2 === 1);
    ws.getRow(r).height = 42;
    r += 1;
  });

  r += 1;
  ws.getCell(`A${r}`).value = "Glossary";
  ws.getCell(`A${r}`).font = font({
    bold: true,
    size: 10,
    color: { argb: BRAND_GREEN },
  });
  r += 1;
  const glossary: [string, string][] = [
    ["Budget", "The appropriation (spending authority) for an object/line."],
    [
      "Committed",
      "A requisition reserved against the budget but not yet a PO.",
    ],
    [
      "Obligated / Encumbered",
      "An open purchase order — money promised to a vendor, not yet paid.",
    ],
    ["Actual", "Cash actually spent (paid invoices)."],
    [
      "Budget Remaining (gross)",
      "Budget − Committed − Obligated − Actual. Overstates spendable; informational.",
    ],
    [
      "Funds Available",
      "The controlling, spendable figure — nets in uncollected revenue. Ties to Oracle.",
    ],
    [
      "Carryover",
      "A PO established in a prior fiscal year still open this year.",
    ],
    [
      "Own-source vs PWC reimbursement",
      "Non-profit revenue earned by DCVFD vs County pass-through; kept separate.",
    ],
  ];
  for (const [term, def] of glossary) {
    ws.getCell(`A${r}`).value = term;
    ws.getCell(`A${r}`).font = font({ bold: true, size: 9 });
    ws.getCell(`A${r}`).alignment = { wrapText: true, vertical: "middle" };
    ws.mergeCells(r, 2, r, 3);
    ws.getCell(`B${r}`).value = def;
    ws.getCell(`B${r}`).alignment = { wrapText: true, vertical: "middle" };
    ws.getCell(`B${r}`).font = font({ size: 9, color: { argb: "FF444444" } });
    r += 1;
  }

  ws.views = [{ state: "frozen", ySplit: 3 }];
}

function buildLevyPosition(
  wb: Workbook,
  ws: Worksheet,
  imageId: number | null,
  data: FinancePackageData,
) {
  const LAST = 7;
  ws.columns = [
    { width: 12 },
    { width: 40 },
    { width: 16 },
    { width: 16 },
    { width: 16 },
    { width: 16 },
    { width: 20 },
  ];
  let r = brandHeader(ws, imageId, "Levy Position", data, LAST);

  const avail = data.available;
  // Lens values: prefer captured authoritative pair; fall back to summary.
  const grossRemaining =
    avail?.expenditure_budget_remaining != null
      ? num(avail.expenditure_budget_remaining)
      : data.summary
        ? num(data.summary.expenditure?.budget) -
          num(data.summary.expenditure?.consumed)
        : null;
  const bceaOnly =
    avail?.expenditure_only_available != null
      ? num(avail.expenditure_only_available)
      : null;
  // Headline = the single-source-of-truth snapshot (ties to Oracle, matches
  // every other surface); fall back to the recon all-object, then the summary.
  const fundsAvailable =
    avail?.snapshot_available != null
      ? num(avail.snapshot_available)
      : avail?.all_object_available != null
        ? num(avail.all_object_available)
        : data.summary
          ? num(data.summary.funds_available)
          : null;

  // Is the gross object roll-up materially ABOVE the BCEA expenditure-only
  // figure? With the authoritative BCA-XML object load they're the same measure
  // (the "Other Anticipated" adjustment is already inside actuals), so we drop
  // the redundant gross lens and show the honest two-lens bridge. Mirrors
  // FundsAvailableReconciler.tsx exactly. If a future source diverges, the
  // three-lens reconciliation returns automatically.
  const gapMaterial =
    grossRemaining != null &&
    bceaOnly != null &&
    grossRemaining - bceaOnly >
      Math.max(1000, Math.abs(fundsAvailable ?? 0) * 0.02);

  sectionTitle(
    ws,
    r,
    gapMaterial
      ? "Funds Available — 3-lens reconciliation"
      : "Funds Available — reconciliation",
  );
  r += 1;

  ws.getCell(`A${r}`).value = "Lens";
  ws.mergeCells(r, 1, r, 5);
  ws.getCell(`F${r}`).value = "Amount";
  ws.mergeCells(r, 6, r, 7);
  styleHeaderRow(ws, r, LAST);
  // right-align the merged amount header
  ws.getCell(`F${r}`).alignment = { horizontal: "right", vertical: "middle" };
  r += 1;

  const revShortfall =
    bceaOnly != null && fundsAvailable != null
      ? bceaOnly - fundsAvailable
      : null;
  const lenses: [string, number | null][] = gapMaterial
    ? [
        ["1 · Budget remaining (gross) — overstates spendable", grossRemaining],
        [
          "2 · BCEA expenditure-only available (County's spending side)",
          bceaOnly,
        ],
        [
          "3 · Funds Available (budgetary control) — the real spendable number",
          fundsAvailable,
        ],
      ]
    : [
        [
          "1 · Expenditure budget available (Budget − spent − open POs)",
          bceaOnly,
        ],
        [
          "2 · less budgeted-but-uncollected revenue (netted in)",
          revShortfall != null ? -revShortfall : null,
        ],
        [
          "3 · Funds Available (budgetary control) — the real spendable number",
          fundsAvailable,
        ],
      ];
  const lastLensIdx = lenses.length - 1;
  lenses.forEach(([label, val], i) => {
    ws.mergeCells(r, 1, r, 5);
    ws.getCell(`A${r}`).value = label;
    ws.getCell(`A${r}`).alignment = { wrapText: true, vertical: "middle" };
    ws.mergeCells(r, 6, r, 7);
    const amt = ws.getCell(`F${r}`);
    amt.value = val != null ? val : "—";
    amt.numFmt = USD_FMT;
    amt.alignment = { horizontal: "right", vertical: "middle" };
    if (i === lastLensIdx) {
      ws.getCell(`A${r}`).font = font({
        bold: true,
        color: { argb: BRAND_GREEN },
      });
      amt.font = font({ bold: true, size: 12, color: { argb: BRAND_GREEN } });
    } else {
      ws.getCell(`A${r}`).font = font({ color: { argb: "FF444444" } });
    }
    styleDataRow(ws, r, LAST, false);
    ws.getRow(r).height = 26;
    r += 1;
  });

  r += 1;
  const fa =
    fundsAvailable != null
      ? `$${Math.round(fundsAvailable).toLocaleString()}`
      : "Funds Available";
  if (gapMaterial) {
    const ga =
      grossRemaining != null
        ? `$${Math.round(grossRemaining).toLocaleString()}`
        : "the gross figure";
    calloutNote(
      ws,
      r,
      LAST,
      `${fa} is what we can actually still spend; ${ga} is gross budget remaining and overstates it. ` +
        "Always quote Funds Available to the Board — the gap is budgeted-but-uncollected recovery revenue.",
    );
  } else {
    const bc =
      bceaOnly != null
        ? `$${Math.round(bceaOnly).toLocaleString()}`
        : "the expenditure budget";
    const rs =
      revShortfall != null
        ? `$${Math.round(revShortfall).toLocaleString()}`
        : "the revenue shortfall";
    calloutNote(
      ws,
      r,
      LAST,
      `${fa} is what we can actually still spend. The expenditure budget alone still shows ${bc}; ` +
        `the controlling figure nets in ${rs} of budgeted-but-uncollected recovery revenue. ` +
        "Always quote Funds Available to the Board.",
    );
  }
  r += 2;

  // ── chart: levy budget vs spent vs available ──
  // Budget = expenditure budget; Spent = consumed (committed+obligated+actual);
  // Available = the controlling Funds Available figure. Drawn only when present.
  const budget = data.summary ? num(data.summary.expenditure?.budget) : null;
  const spent = data.summary ? num(data.summary.expenditure?.consumed) : null;
  if (budget != null && spent != null && fundsAvailable != null) {
    const buf = renderBarChart(
      "Levy — budget vs spent vs available",
      ["Budget", "Spent (consumed)", "Funds Available"],
      [{ color: CHART_GREEN_MID, values: [budget, spent, fundsAvailable] }],
    );
    r = embedChart(wb, ws, buf, r);
  }

  sectionTitle(ws, r, "By object — budgetary control");
  r += 1;

  const cols = [
    "Object",
    "Description",
    "Budget",
    "Committed",
    "Obligated",
    "Actual",
    "Budget Remaining (gross)",
  ];
  cols.forEach((c, i) => (ws.getCell(r, i + 1).value = c));
  styleHeaderRow(ws, r, LAST);
  r += 1;

  const rows = data.byObject;
  if (!rows) {
    syncingNote(ws, r, "The levy by-object table");
  } else if (rows.length === 0) {
    ws.getCell(`A${r}`).value = "—";
  } else {
    const start = r;
    rows.forEach((row, i) => {
      ws.getCell(r, 1).value = row.object_segment;
      ws.getCell(r, 2).value = row.object_desc;
      const nums = [
        num(row.budget_amount),
        num(row.commitment_amount),
        num(row.obligation_amount),
        num(row.actual_amount),
        num(row.budget_amount) -
          num(row.commitment_amount) -
          num(row.obligation_amount) -
          num(row.actual_amount),
      ];
      nums.forEach((v, j) => {
        const cell = ws.getCell(r, j + 3);
        cell.value = v;
        cell.numFmt = USD_FMT;
        cell.alignment = { horizontal: "right", vertical: "middle" };
      });
      styleDataRow(ws, r, LAST, i % 2 === 1);
      r += 1;
    });
    // totals / rollup row — light-green band
    ws.getCell(r, 2).value = "TOTAL";
    for (let c = 3; c <= 7; c++) {
      const colLetter = ws.getColumn(c).letter;
      const cell = ws.getCell(r, c);
      cell.value = {
        formula: `SUM(${colLetter}${start}:${colLetter}${r - 1})`,
      };
      cell.numFmt = USD_FMT;
      cell.alignment = { horizontal: "right", vertical: "middle" };
    }
    styleRollupRow(ws, r, LAST);
  }

  ws.views = [{ state: "frozen", ySplit: 3 }];
}

function buildOpenPos(
  ws: Worksheet,
  imageId: number | null,
  data: FinancePackageData,
) {
  const LAST = 6;
  ws.columns = [
    { width: 16 },
    { width: 34 },
    { width: 30 },
    { width: 18 },
    { width: 14 },
    { width: 18 },
  ];
  let r = brandHeader(ws, imageId, "Open Purchase Orders", data, LAST);

  sectionTitle(ws, r, "Open-encumbrance register");
  r += 1;
  ws.mergeCells(r, 1, r, LAST);
  ws.getCell(r, 1).value =
    "Every open purchase order (money promised to vendors, not yet paid). " +
    "Run these down before fiscal close (June 30). Carryover = established in a prior FY.";
  ws.getCell(r, 1).font = font({
    size: 9,
    italic: true,
    color: { argb: "FF666666" },
  });
  ws.getCell(r, 1).alignment = { wrapText: true, vertical: "middle" };
  r += 2;

  const cols = [
    "PO #",
    "Supplier",
    "Object",
    "Remaining",
    "Carryover",
    "Status",
  ];
  cols.forEach((c, i) => (ws.getCell(r, i + 1).value = c));
  styleHeaderRow(ws, r, LAST);
  r += 1;

  const rows = data.openPos;
  if (!rows) {
    syncingNote(ws, r, "The open-PO register");
  } else if (rows.length === 0) {
    ws.getCell(`A${r}`).value = "—";
  } else {
    const start = r;
    rows.forEach((row, i) => {
      ws.getCell(r, 1).value = row.po_number;
      ws.getCell(r, 2).value = row.supplier;
      ws.getCell(r, 3).value = row.object_desc;
      const rem = ws.getCell(r, 4);
      rem.value = num(row.remaining);
      rem.numFmt = USD_FMT;
      rem.alignment = { horizontal: "right", vertical: "middle" };
      // Carryover = the PO-number suffix is a PRIOR fiscal year (op.carryover,
      // computed server-side). Do NOT also OR in the stored `carryforward` flag —
      // that one is budget_date-based and misclassifies POs re-budgeted at the
      // 7/1 FY boundary. The suffix is the reliable signal (matches the PO page).
      ws.getCell(r, 5).value = num(row.carryover) === 1 ? "Yes" : "";
      ws.getCell(r, 5).alignment = { horizontal: "center", vertical: "middle" };
      ws.getCell(r, 6).value = row.status;
      styleDataRow(ws, r, LAST, i % 2 === 1);
      r += 1;
    });
    // Per-appropriation subtotals BEFORE the grand total, so this sheet
    // self-reconciles with the Levy Position tab: the operating-fund subtotal
    // equals the by-object obligation sum; the fund-2101 carryover rides its
    // own swept appropriation and is disclosed, never blended.
    const opFund = rows.filter((x) => (x.fund ?? "2119") !== "2101");
    const carryFund = rows.filter((x) => x.fund === "2101");
    const subtotal = (label: string, set: typeof rows) => {
      ws.getCell(r, 2).value = `${label} (${set.length} POs)`;
      const c = ws.getCell(r, 4);
      c.value = set.reduce((s, x) => s + num(x.remaining), 0);
      c.numFmt = USD_FMT;
      c.alignment = { horizontal: "right", vertical: "middle" };
      styleRollupRow(ws, r, LAST);
      r += 1;
    };
    if (carryFund.length > 0) {
      subtotal("Operating fund 2119 — ties Levy Position obligations", opFund);
      subtotal(
        "FY26 carryover, fund 2101 — own swept appropriation",
        carryFund,
      );
    }
    ws.getCell(r, 2).value = `TOTAL (${rows.length} POs)`;
    const cell = ws.getCell(r, 4);
    cell.value = { formula: `SUM(D${start}:D${start + rows.length - 1})` };
    cell.numFmt = USD_FMT;
    cell.alignment = { horizontal: "right", vertical: "middle" };
    styleRollupRow(ws, r, LAST);
  }

  ws.views = [{ state: "frozen", ySplit: 3 }];
}

function buildFy27V2(
  wb: Workbook,
  ws: Worksheet,
  imageId: number | null,
  data: FinancePackageData,
) {
  const LAST = 8;
  ws.columns = [
    { width: 26 }, // Committee / Category
    { width: 34 }, // Expense type
    { width: 13 }, // FY24
    { width: 13 }, // FY25
    { width: 13 }, // FY26
    { width: 14 }, // FY27 v2
    { width: 13 }, // v1 FY27
    { width: 12 }, // Δ
  ];
  let r = brandHeader(
    ws,
    imageId,
    `${data.nextFy} Budget v2 — 06-JUL revision`,
    data,
    LAST,
  );
  const v2 = data.fy27V2;

  const usdc = (n: number) =>
    `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  sectionTitle(
    ws,
    r,
    `${data.nextFy} v2 recurring operating (revision to the 15-JUN v1)`,
  );
  r += 1;
  ws.mergeCells(r, 1, r, LAST);
  ws.getCell(r, 1).value = v2
    ? `Three labeled layers per committee — never blended: Recurring v2 ` +
      `$${Math.round(num(v2.grand.recurring)).toLocaleString()} ` +
      `(v1 15-JUN $${Math.round(num(v2.grand.v1_recurring)).toLocaleString()}; ` +
      `Δ ${v2.grand.delta >= 0 ? "+" : ""}$${Math.round(num(v2.grand.delta)).toLocaleString()}) ` +
      `+ Funded projects $${Math.round(num(v2.grand.projects_funded)).toLocaleString()} ` +
      `+ ${v2.labels.po_carryover} ${usdc(num(v2.grand.po_carryover))} ` +
      `(${v2.grand.po_carryover_count} POs) = GRAND ALL-IN ${usdc(num(v2.grand.all_in))}. ` +
      "Structure: Committee → Category → Expense type (the board's decision unit); suppliers/OCAs are backing detail. " +
      "Every FY24/25/26 actual ties to the GL. " +
      v2.labels.po_carryover_footnote +
      " Arrears exposure is informational and excluded from every sum."
    : "FY27 v2 is syncing.";
  ws.getCell(r, 1).font = font({
    size: 9,
    italic: true,
    color: { argb: "FF666666" },
  });
  ws.getCell(r, 1).alignment = { wrapText: true, vertical: "middle" };
  ws.getRow(r).height = 64;
  r += 2;

  const cols = [
    "Committee / Category",
    "Expense type",
    "FY24 Actual",
    "FY25 Actual",
    "FY26 Actual",
    `${data.nextFy} v2`,
    "v1 (15-JUN)",
    "Δ v1→v2",
  ];
  cols.forEach((c, i) => (ws.getCell(r, i + 1).value = c));
  styleHeaderRow(ws, r, LAST);
  r += 1;

  if (!v2) {
    syncingNote(ws, r, "The FY27 v2 budget");
    return;
  }

  // A merged label row with an amount in col 6. `cents` = cent-exact format.
  const layerRow = (
    label: string,
    amount: number | null,
    opts: {
      bold?: boolean;
      italic?: boolean;
      cents?: boolean;
      fill?: boolean;
    } = {},
  ) => {
    ws.getCell(r, 1).value = label;
    ws.mergeCells(r, 1, r, 5);
    ws.getCell(r, 1).alignment = { wrapText: true, vertical: "middle" };
    if (amount != null) {
      ws.getCell(r, 6).value = amount;
      ws.getCell(r, 6).numFmt = opts.cents ? USD_CENTS_FMT : USD_FMT;
    }
    for (let c = 1; c <= LAST; c++) {
      ws.getCell(r, c).font = font({
        bold: opts.bold,
        italic: opts.italic,
        size: opts.italic ? 8 : undefined,
        color: opts.italic ? { argb: "FF8A6D3B" } : undefined,
      });
      if (opts.fill)
        ws.getCell(r, c).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: ROLLUP_FILL },
        };
    }
    r += 1;
  };

  for (const cmte of v2.committees) {
    // committee rollup band — recurring v2 / v1 CONTROL / delta (vs control)
    ws.getCell(r, 1).value = `${cmte.committee}`;
    ws.mergeCells(r, 1, r, 5);
    const ct = ws.getCell(r, 6);
    ct.value = num(cmte.recurring);
    ct.numFmt = USD_FMT;
    ws.getCell(r, 7).value = num(cmte.v1_fy27);
    ws.getCell(r, 7).numFmt = USD_FMT;
    ws.getCell(r, 8).value = num(cmte.delta_v1);
    ws.getCell(r, 8).numFmt = USD_FMT;
    for (let c = 1; c <= LAST; c++) {
      ws.getCell(r, c).font = font({ bold: true });
      ws.getCell(r, c).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: ROLLUP_FILL },
      };
    }
    r += 1;
    for (const cat of cmte.categories) {
      ws.getCell(r, 1).value = `  ${cat.category}`;
      ws.getCell(r, 1).font = font({
        size: 9,
        bold: true,
        color: { argb: "FF3A4250" },
      });
      r += 1;
      for (const l of cat.lines) {
        ws.getCell(r, 2).value =
          l.expense_type + (l.needs_director_confirm ? "  ⚑" : "");
        ws.getCell(r, 3).value = num(l.fy24_actual);
        ws.getCell(r, 4).value = num(l.fy25_actual);
        ws.getCell(r, 5).value = num(l.fy26_actual);
        ws.getCell(r, 6).value = num(l.fy27_v2);
        for (let c = 3; c <= 6; c++) ws.getCell(r, c).numFmt = USD_FMT;
        // G-4: provenance = supplier_detail (+ reasoning on the page). The
        // `backing` column is unpopulated (0/58) and is NOT rendered.
        if (l.supplier_detail) {
          ws.getCell(r, 7).value = String(l.supplier_detail).slice(0, 60);
          ws.getCell(r, 7).font = font({
            size: 8,
            color: { argb: "FF9AA0A6" },
          });
          ws.mergeCells(r, 7, r, 8);
        }
        r += 1;
      }
    }
    if (cmte.categories.length > 0)
      layerRow(`  ${cmte.committee} — Recurring v2`, num(cmte.recurring), {
        bold: true,
      });

    // Layer 2 — funded one-time projects (additive).
    layerRow(
      `  Funded projects (one-time — additive)`,
      num(cmte.projects_funded_total),
      { bold: true },
    );
    for (const p of cmte.projects_funded)
      layerRow(
        `    ${p.ref ? p.ref + " " : ""}${p.name} — ${p.funding_source || "—"} · ${p.status || "—"}`,
        num(p.amount),
      );
    // Operating-levy projects live inside the recurring lines — amount stays in
    // the label so the additive column can never double-count them.
    for (const p of cmte.projects_in_recurring)
      layerRow(
        `    ${p.ref ? p.ref + " " : ""}${p.name} — $${Math.round(num(p.amount)).toLocaleString()} (in recurring above — not re-added)`,
        null,
        { italic: true },
      );
    for (const p of cmte.projects_not_funded)
      layerRow(
        `    Not funded: ${p.ref ? p.ref + " " : ""}${p.name} $${Math.round(num(p.amount)).toLocaleString()} — ${p.status}`,
        null,
        { italic: true },
      );

    // Layer 3 — FY26→FY27 open-PO carryover (confirmed at FY close).
    layerRow(`  ${v2.labels.po_carryover}`, num(cmte.po_carryover.total), {
      bold: true,
      cents: true,
    });
    const topPos = cmte.po_carryover.pos.slice(0, 5);
    for (const po of topPos)
      layerRow(
        `    ${po.po_number} — ${po.supplier || "(supplier not in register)"}`,
        num(po.remaining),
        { cents: true },
      );
    const rest = cmte.po_carryover.pos.slice(5);
    if (rest.length > 0)
      layerRow(
        `    (+${rest.length} more POs)`,
        rest.reduce((s, po) => s + num(po.remaining), 0),
        { cents: true },
      );

    // Arrears — informational sub-line ONLY; never summed into any layer.
    if (cmte.arrears_note)
      layerRow(`  ${cmte.arrears_note}`, null, { italic: true });

    // G-3: the labeled 3-layer ALL-IN.
    layerRow(
      `  ${cmte.committee} ALL-IN (Recurring v2 + Funded projects + Open-PO carryover)`,
      num(cmte.all_in),
      { bold: true, cents: true, fill: true },
    );
  }

  // grand totals — the three layers + ALL-IN, each labeled (never blended)
  ws.getCell(r, 1).value = "RECURRING TOTAL";
  ws.mergeCells(r, 1, r, 5);
  ws.getCell(r, 6).value = num(v2.grand.recurring);
  ws.getCell(r, 6).numFmt = USD_FMT;
  ws.getCell(r, 7).value = num(v2.grand.v1_recurring);
  ws.getCell(r, 7).numFmt = USD_FMT;
  ws.getCell(r, 8).value = num(v2.grand.delta);
  ws.getCell(r, 8).numFmt = USD_FMT;
  for (let c = 1; c <= LAST; c++) ws.getCell(r, c).font = font({ bold: true });
  r += 1;
  layerRow(
    "FUNDED PROJECTS TOTAL (one-time — additive)",
    num(v2.grand.projects_funded),
    {
      bold: true,
    },
  );
  layerRow(
    `${v2.labels.po_carryover} (${v2.grand.po_carryover_count} POs)`,
    num(v2.grand.po_carryover),
    { bold: true, cents: true },
  );
  layerRow(
    "GRAND ALL-IN (Recurring v2 + Funded projects + Open-PO carryover)",
    num(v2.grand.all_in),
    { bold: true, cents: true, fill: true },
  );
  r += 1;

  // Projects schedule (one-time / capital), grouped by funding source — this is
  // where the capital items live (Verkada / Ambulance / Small Vehicle #1, etc.).
  if (v2.projects && v2.projects.length) {
    sectionTitle(
      ws,
      r,
      "Projects schedule (one-time / capital — by funding source)",
    );
    r += 1;
    const order = [
      "Fund balance",
      "Operating levy",
      "Insurance",
      "Rejected",
      "Deferred",
    ];
    const rank = (s: string) => {
      const i = order.indexOf(s);
      return i === -1 ? order.length : i;
    };
    const sources = [
      ...new Set(v2.projects.map((p) => p.funding_source || "(unassigned)")),
    ].sort((a, b) => rank(a) - rank(b));
    for (const src of sources) {
      const rows = v2.projects.filter(
        (p) => (p.funding_source || "(unassigned)") === src,
      );
      const tot = rows.reduce((s, p) => s + num(p.amount), 0);
      ws.getCell(r, 1).value = src;
      ws.mergeCells(r, 1, r, 5);
      ws.getCell(r, 6).value = tot;
      ws.getCell(r, 6).numFmt = USD_FMT;
      for (let c = 1; c <= LAST; c++)
        ws.getCell(r, c).font = font({ bold: true });
      r += 1;
      for (const p of rows) {
        const tag = p.affects_operating ? " (in recurring)" : "";
        ws.getCell(r, 1).value = `  ${p.ref ? p.ref + " " : ""}${p.name}${tag}`;
        ws.mergeCells(r, 1, r, 4);
        ws.getCell(r, 5).value = p.status || "";
        ws.getCell(r, 6).value = num(p.amount);
        ws.getCell(r, 6).numFmt = USD_FMT;
        r += 1;
      }
    }
  }
  void wb;
}

const INITIATIVE_FUNDING: Record<string, string> = {
  "27.001": "Insurance / restricted (up to +$50k operating)",
  "27.002": "Rejected (base operating increase not funded)",
  "27.003": "Operating shift (Temp→FTE; net savings, not new money)",
  "27.004": "Fund balance / restricted",
  "27.005": "Operating",
  "27.006": "N/A — already purchased FY26 (record only)",
  "27.007": "Operating",
};

function buildInitiatives(
  ws: Worksheet,
  imageId: number | null,
  data: FinancePackageData,
) {
  const LAST = 6;
  ws.columns = [
    { width: 10 },
    { width: 34 },
    { width: 22 },
    { width: 16 },
    { width: 16 },
    { width: 36 },
  ];
  let r = brandHeader(ws, imageId, `${data.nextFy} Initiatives`, data, LAST);

  sectionTitle(ws, r, "County initiatives (Phase I forms 27.001–27.007)");
  r += 1;
  ws.mergeCells(r, 1, r, LAST);
  ws.getCell(r, 1).value =
    "Submitted to PWC/Kara Bonkoski 9/2/2025; compiled to the BOD by Chief Hool 6/11/2026. " +
    "Decision = the project's current review status; funding source per Rubino 9/16/2025.";
  ws.getCell(r, 1).font = font({
    size: 9,
    italic: true,
    color: { argb: "FF666666" },
  });
  ws.getCell(r, 1).alignment = { wrapText: true, vertical: "middle" };
  r += 1;

  // Additive callout: the approved operating initiatives layer on via the Bridge
  // — they are NOT inside the committee operating base on the Budget tab.
  calloutNote(
    ws,
    r,
    LAST,
    `Approved OPERATING initiatives are additive via the ${data.nextFy} Bridge — they are NOT inside the ` +
      "committee operating base on the Budget tab. Operating adds: 27.005 Small Vehicle #2 $75k + 27.007 Bay " +
      "Paint $30k = $105k; plus the 27.003 R&R $80k shift. 27.004 ($65k) is fund-balance and 27.001 ($332k) is " +
      "insurance — both funded OUTSIDE operating; 27.002 ($500k) was REJECTED. No initiative is double-counted in the base.",
  );
  ws.getRow(r).height = 56;
  r += 2;

  const cols = [
    "#",
    "Initiative",
    "Committee",
    "Amount",
    "Decision",
    "Funding source",
  ];
  cols.forEach((c, i) => (ws.getCell(r, i + 1).value = c));
  styleHeaderRow(ws, r, LAST);
  r += 1;

  const projects = data.projects;
  if (!projects) {
    syncingNote(ws, r, "The FY27 initiatives list");
  } else {
    const inits = projects
      .filter((p) => /^27\.0\d/.test(String(p.name)))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    if (inits.length === 0) {
      ws.getCell(`A${r}`).value = "—";
    } else {
      inits.forEach((p, i) => {
        const m = String(p.name).match(/^(27\.0\d\d)\s+(.*)$/);
        const code = m ? m[1] : String(p.name).slice(0, 6);
        const title = m ? m[2] : String(p.name);
        ws.getCell(r, 1).value = code;
        ws.getCell(r, 2).value = title;
        ws.getCell(r, 2).alignment = { wrapText: true, vertical: "middle" };
        ws.getCell(r, 3).value = p.committee ?? "—";
        const amt = ws.getCell(r, 4);
        amt.value = num(p.cost_estimate);
        amt.numFmt = USD_FMT;
        amt.alignment = { horizontal: "right", vertical: "middle" };
        ws.getCell(r, 5).value = p.status ?? "proposed";
        ws.getCell(r, 6).value = INITIATIVE_FUNDING[code] ?? "—";
        ws.getCell(r, 6).alignment = { wrapText: true, vertical: "middle" };
        styleDataRow(ws, r, LAST, i % 2 === 1);
        r += 1;
      });
    }
  }

  ws.views = [{ state: "frozen", ySplit: 3 }];
}

function buildNonProfit(
  ws: Worksheet,
  imageId: number | null,
  data: FinancePackageData,
) {
  const LAST = 4;
  ws.columns = [{ width: 38 }, { width: 18 }, { width: 18 }, { width: 24 }];
  let r = brandHeader(ws, imageId, "Non-Profit (QBO)", data, LAST);

  const np = data.nonprofit;
  sectionTitle(ws, r, "DCVFD 501(c)(4) operating book (QuickBooks / Truist)");
  r += 1;
  ws.mergeCells(r, 1, r, LAST);
  ws.getCell(r, 1).value =
    "The non-profit operating book — SEPARATE from the County levy. Restricted " +
    "(payroll) cash is excluded from Cash Available. PWC reimbursement is a " +
    "pass-through, kept separate from own-source revenue.";
  ws.getCell(r, 1).font = font({
    size: 9,
    italic: true,
    color: { argb: "FF666666" },
  });
  ws.getCell(r, 1).alignment = { wrapText: true, vertical: "middle" };
  r += 2;

  if (!np || !np.connected) {
    syncingNote(
      ws,
      r,
      np && !np.connected
        ? "QuickBooks is not connected, so the non-profit book"
        : "The non-profit (QBO) summary",
    );
    ws.views = [{ state: "frozen", ySplit: 3 }];
    return;
  }

  // KPI block
  const cashAvail = np.cash_available;
  const cashRestricted = np.cash_restricted;
  const cashTotal = np.cash_total;
  const kpis: [string, number | null][] = [
    ["Cash Available (net of payroll set-aside)", cashAvail],
    ["Restricted (payroll) cash — excluded", cashRestricted],
    ["Cash Total (all accounts)", cashTotal],
    ["Accounts Receivable", np.ar_total],
  ];
  ws.getCell(r, 1).value = "Cash position";
  ws.getCell(r, 1).font = font({ bold: true, color: { argb: BRAND_GREEN } });
  r += 1;
  for (const [label, val] of kpis) {
    ws.getCell(r, 1).value = label;
    const a = ws.getCell(r, 2);
    a.value = val != null ? num(val) : "—";
    a.numFmt = USD_FMT;
    a.alignment = { horizontal: "right", vertical: "middle" };
    styleDataRow(ws, r, LAST, false);
    r += 1;
  }
  r += 1;

  // Bank accounts
  sectionTitle(ws, r, "Bank accounts");
  r += 1;
  ["Account", "Number", "Balance", "Restricted"].forEach(
    (c, i) => (ws.getCell(r, i + 1).value = c),
  );
  styleHeaderRow(ws, r, LAST);
  r += 1;
  if (np.bank_accounts.length === 0) {
    ws.getCell(`A${r}`).value = "—";
    r += 1;
  } else {
    np.bank_accounts.forEach((acct, i) => {
      ws.getCell(r, 1).value = acct.name;
      ws.getCell(r, 2).value = acct.acct_num;
      const bal = ws.getCell(r, 3);
      bal.value = num(acct.balance);
      bal.numFmt = USD_FMT;
      bal.alignment = { horizontal: "right", vertical: "middle" };
      ws.getCell(r, 4).value = acct.restricted
        ? "Set-aside"
        : acct.reconciling
          ? "Reconciling"
          : "";
      ws.getCell(r, 4).alignment = { horizontal: "center", vertical: "middle" };
      styleDataRow(ws, r, LAST, i % 2 === 1);
      r += 1;
    });
  }
  r += 1;

  // Income by source — split into TWO visually distinct blocks so a reader can
  // never mistake the PWC pass-through for the org's own spend:
  //   (A) Reimbursed (levy pass-through)  — PWC reimbursement in/out, nets to ~0
  //   (B) Own-funded / non-levy           — own-source revenue + own-funded spend
  sectionTitle(ws, r, "Income by source — pass-through vs own-funded");
  r += 1;
  const inc = data.npIncome;
  if (!inc || !inc.available) {
    ["Source", "Amount", "Type", ""].forEach(
      (c, i) => (ws.getCell(r, i + 1).value = c),
    );
    styleHeaderRow(ws, r, LAST);
    r += 1;
    syncingNote(ws, r, "Income-by-source");
    r += 1;
  } else {
    const pwcRows = inc.rows.filter((row) => row.is_pwc);
    const ownRows = inc.rows.filter((row) => !row.is_pwc);

    // ── Block A: Reimbursed (levy pass-through) ──
    ws.getCell(r, 1).value = "A · Reimbursed (levy pass-through)";
    ws.mergeCells(r, 1, r, LAST);
    styleRollupRow(ws, r, LAST);
    r += 1;
    ws.mergeCells(r, 1, r, LAST);
    ws.getCell(r, 1).value =
      "PWC reimbursement is County money flowing through DCVFD — NOT the org's own revenue or spend. " +
      "It washes through (reimbursement in ≈ levy expense out) and is kept entirely separate below.";
    ws.getCell(r, 1).font = font({
      size: 9,
      italic: true,
      color: { argb: "FF666666" },
    });
    ws.getCell(r, 1).alignment = { wrapText: true, vertical: "middle" };
    ws.getRow(r).height = 42;
    r += 1;
    ["Source", "Amount", "Type", ""].forEach(
      (c, i) => (ws.getCell(r, i + 1).value = c),
    );
    styleHeaderRow(ws, r, LAST);
    r += 1;
    if (pwcRows.length === 0) {
      ws.getCell(`A${r}`).value = "—";
      r += 1;
    } else {
      pwcRows.forEach((row, i) => {
        ws.getCell(r, 1).value = row.source;
        const a = ws.getCell(r, 2);
        a.value = num(row.amount);
        a.numFmt = USD_FMT;
        a.alignment = { horizontal: "right", vertical: "middle" };
        ws.getCell(r, 3).value = "PWC reimbursement";
        styleDataRow(ws, r, LAST, i % 2 === 1);
        r += 1;
      });
    }
    ws.getCell(r, 1).value = "Reimbursed (pass-through) subtotal";
    const pwcSub = ws.getCell(r, 2);
    pwcSub.value = num(inc.pwc_total);
    pwcSub.numFmt = USD_FMT;
    pwcSub.alignment = { horizontal: "right", vertical: "middle" };
    styleRollupRow(ws, r, LAST);
    r += 2;

    // ── Block B: Own-funded / non-levy ──
    ws.getCell(r, 1).value =
      "B · Own-funded / non-levy (real 501(c)(4) activity)";
    ws.mergeCells(r, 1, r, LAST);
    styleRollupRow(ws, r, LAST);
    r += 1;
    ["Source", "Amount", "Type", ""].forEach(
      (c, i) => (ws.getCell(r, i + 1).value = c),
    );
    styleHeaderRow(ws, r, LAST);
    r += 1;
    if (ownRows.length === 0) {
      ws.getCell(`A${r}`).value = "—";
      r += 1;
    } else {
      ownRows.forEach((row, i) => {
        ws.getCell(r, 1).value = row.source;
        const a = ws.getCell(r, 2);
        a.value = num(row.amount);
        a.numFmt = USD_FMT;
        a.alignment = { horizontal: "right", vertical: "middle" };
        ws.getCell(r, 3).value = "Own-source";
        styleDataRow(ws, r, LAST, i % 2 === 1);
        r += 1;
      });
    }
    ws.getCell(r, 1).value = "Own-source revenue subtotal";
    const ownSub = ws.getCell(r, 2);
    ownSub.value = num(inc.own_total);
    ownSub.numFmt = USD_FMT;
    ownSub.alignment = { horizontal: "right", vertical: "middle" };
    styleRollupRow(ws, r, LAST);
    r += 1;

    // own-funded spend = total expenses − PWC reimbursement (pass-through out)
    const ownFunded =
      np.expense_total != null
        ? num(np.expense_total) - num(inc.pwc_total)
        : null;
    ws.getCell(r, 1).value =
      "Own-funded / non-levy spend (expenses − PWC reimbursement)";
    const of = ws.getCell(r, 2);
    of.value = ownFunded != null ? ownFunded : "n/a";
    of.numFmt = USD_FMT;
    of.alignment = { horizontal: "right", vertical: "middle" };
    styleRollupRow(ws, r, LAST);
    r += 1;

    // memo: total income (own + pass-through) for tie-out only
    ws.getCell(r, 1).value = "Memo — total income (own + pass-through)";
    ws.getCell(r, 1).font = font({ size: 9, color: { argb: "FF666666" } });
    const tot = ws.getCell(r, 2);
    tot.value = num(inc.total);
    tot.numFmt = USD_FMT;
    tot.font = font({ size: 9, color: { argb: "FF666666" } });
    tot.alignment = { horizontal: "right", vertical: "middle" };
    r += 1;
  }
  r += 1;

  // Expenses by category
  sectionTitle(ws, r, "Expenses by category");
  r += 1;
  const exp = data.npExpenses;
  ["Category", "Account", "Amount", ""].forEach(
    (c, i) => (ws.getCell(r, i + 1).value = c),
  );
  styleHeaderRow(ws, r, LAST);
  r += 1;
  if (!exp || !exp.available) {
    syncingNote(ws, r, "Expenses-by-category");
  } else {
    const start = r;
    exp.rows.forEach((row, i) => {
      ws.getCell(r, 1).value = row.category;
      ws.getCell(r, 2).value = row.acct_num;
      const a = ws.getCell(r, 3);
      a.value = num(row.amount);
      a.numFmt = USD_FMT;
      a.alignment = { horizontal: "right", vertical: "middle" };
      styleDataRow(ws, r, LAST, i % 2 === 1);
      r += 1;
    });
    ws.getCell(r, 1).value = "TOTAL";
    const t = ws.getCell(r, 3);
    t.value = { formula: `SUM(C${start}:C${r - 1})` };
    t.numFmt = USD_FMT;
    t.alignment = { horizontal: "right", vertical: "middle" };
    styleRollupRow(ws, r, LAST);
  }

  ws.views = [{ state: "frozen", ySplit: 3 }];
}

// ── derived QBO metrics (computed here from loaded data; never fabricated) ─────
export interface QboMetric {
  label: string;
  value: string;
  formula: string;
  /** True when the figure exists but is deliberately held off the board. */
  withheld?: boolean;
}

// ── Net Operating / Net Income hold (finance control C-4, no-bad-number) ──────
//
// QuickBooks books the county payroll advance as a credit against EXPENSE
// account 55601 (Payroll — Employee Subsidy) instead of against the payroll
// liability. That drives the period expense total NEGATIVE, so income −
// expenses reads as a large positive "surplus" the department did not earn.
// Finance ruled the figure is held off the board until the bookkeeper explains
// and, if required, reclassifies the entry.
//
// FinanceNonProfit.tsx applies the identical hold on screen (`NET_KPI_HOLD` /
// `netKpiState` there). This is the workbook half of the same ruling: the
// artifact that is actually mailed to the Board must not carry a number the
// screen withholds. Clear BOTH together, and only on finance's instruction —
// never on inference from a code change.
//
// NOTE: `NonprofitSummary.net` is NOT an independent QBO figure. The summary
// API computes it as income_total − expense_total (finance-nonprofit-api.ts),
// so "Net income (period)" is arithmetically the same number as "Net operating
// (period)" and carries exactly the same distortion. Both are held.
export const NET_KPI_HOLD = true;

/** What a held net figure prints as. Never a number, never a substitute. */
export const NET_KPI_WITHHELD_VALUE = "Withheld";

/** The basis text that replaces the formula for a held row. Kept SELF-CONTAINED
 *  because financePackagePdf.ts renders this column too but has no room for the
 *  longer callout — the reason must travel with the row, not sit beside it. */
export const NET_KPI_WITHHELD_BASIS =
  "WITHHELD pending explanation — this is not zero and not a break-even. " +
  "QuickBooks credits the county payroll advance to EXPENSE account 55601 " +
  "(Payroll — Employee Subsidy) instead of the payroll liability, which makes " +
  "income − expenses read as a surplus the department did not earn.";

/** The board-facing reason, rendered as a callout under the metrics table. */
export const NET_KPI_HOLD_NOTE =
  "NET OPERATING AND NET INCOME ARE WITHHELD FROM THIS PACKAGE. QuickBooks " +
  "currently books the county payroll advance as a credit against expense " +
  "account 55601 (Payroll — Employee Subsidy) rather than against the payroll " +
  "liability. That pushes the reported period expense total negative, so " +
  "income − expenses would read as a large positive surplus the department " +
  "did not earn. Both rows are held until the bookkeeper explains and, if " +
  "required, reclassifies the entry. Note that the reported 'net income' is " +
  "derived as income − expenses, so it carries the same distortion — it is " +
  "not an independent check. The Income and Expenses figures elsewhere in " +
  "this package are shown exactly as QuickBooks reports them and carry the " +
  "same contra-credit. A withheld figure means UNKNOWN, not zero.";

/** The same warning, sized for the monthly trend block. */
export const NET_KPI_TREND_NOTE =
  "QUALIFIED: the Net column and the Net series in the chart below are shown " +
  "as QuickBooks reports them. The month carrying the account 55601 " +
  "contra-credit (see the note above) shows a large positive net that the " +
  "department did not earn. Do not read that spike as a surplus.";

/**
 * Whether a net figure must be withheld from the board.
 *
 * - `value == null` → NOT withheld. There is nothing to hold; the row renders
 *   the usual honest "n/a". "Withheld" must never be used to describe data we
 *   simply do not have.
 * - Finance hold active → withheld.
 * - Defence in depth, independent of the flag: a negative period expense total
 *   means the expense side carries a contra-credit, so income − expenses is not
 *   an operating result and must not reach the Board as one.
 */
export function netKpiWithheld(
  np: NonprofitSummary | null,
  value: number | null,
): boolean {
  if (value == null) return false;
  if (NET_KPI_HOLD) return true;
  return np?.expense_total != null && num(np.expense_total) < 0;
}

/** Compute the BOD financial metrics from the QBO summary + timeseries. Pure
 *  (exported for unit testing). Any metric whose inputs are missing returns
 *  "n/a" — we never invent a denominator. */
export function computeQboMetrics(
  np: NonprofitSummary | null,
  ts: NonprofitTimeseries | null,
): QboMetric[] {
  const out: QboMetric[] = [];
  const fmtUsd = (n: number) =>
    n.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

  // Current ratio = current assets ÷ current liabilities. The summary exposes
  // cash + AR (current assets) but NOT current liabilities, so this is n/a
  // unless that ever lands. We surface the current-assets components for the
  // Board and mark the ratio n/a honestly.
  const currentAssets =
    np && (np.cash_total != null || np.ar_total != null)
      ? num(np.cash_total) + num(np.ar_total)
      : null;
  out.push({
    label: "Current assets (cash + A/R)",
    value: currentAssets != null ? fmtUsd(currentAssets) : "n/a",
    formula: "cash_total + ar_total",
  });
  out.push({
    label: "Current ratio",
    value: "n/a",
    formula:
      "current assets ÷ current liabilities — liabilities not exposed by QBO summary; n/a",
  });

  // Cash position & runway. Runway = available cash ÷ avg monthly expense
  // (trailing 12 mo from the timeseries).
  const cashAvail = np?.cash_available ?? null;
  out.push({
    label: "Cash available",
    value: cashAvail != null ? fmtUsd(num(cashAvail)) : "n/a",
    formula: "cash_total − payroll set-aside",
  });
  let avgMonthlyExpense: number | null = null;
  if (ts?.available && ts.months.length > 0) {
    const recent = ts.months.slice(-12);
    const totalExp = recent.reduce((s, m) => s + num(m.expense), 0);
    avgMonthlyExpense = recent.length > 0 ? totalExp / recent.length : null;
  }
  const runway =
    cashAvail != null && avgMonthlyExpense != null && avgMonthlyExpense > 0
      ? num(cashAvail) / avgMonthlyExpense
      : null;
  // Cash runway divides by the trailing-12 average monthly EXPENSE, which carries
  // the same 55601 contra-credit that forces Net operating / Net income to be held:
  // the Jul-2026 expense column is NEGATIVE (-$451,284.13), so it drags the average
  // down and inflates the runway. Withholding the two net rows while publishing a
  // runway derived from the same distorted series would just move the bad number to
  // a different line of the same page. Held on the same flag, for the same reason.
  out.push({
    label: "Cash runway (months)",
    value:
      NET_KPI_HOLD || runway == null ? "Withheld" : `${runway.toFixed(1)} mo`,
    formula: NET_KPI_HOLD
      ? "cash available ÷ avg monthly expense (trailing 12 mo) — " +
        NET_KPI_WITHHELD_BASIS
      : "cash available ÷ avg monthly expense (trailing 12 mo)",
  });

  // Net operating (current period from summary) + net income if available.
  // Both are subject to the finance hold above — see NET_KPI_HOLD. When held
  // they print "Withheld" with a visible reason; they are never suppressed
  // silently and never replaced with a substituted number.
  const netOperating =
    np && np.income_total != null && np.expense_total != null
      ? num(np.income_total) - num(np.expense_total)
      : null;
  const netOperatingHeld = netKpiWithheld(np, netOperating);
  out.push({
    label: "Net operating (period)",
    value: netOperatingHeld
      ? NET_KPI_WITHHELD_VALUE
      : netOperating != null
        ? fmtUsd(netOperating)
        : "n/a",
    formula: netOperatingHeld
      ? NET_KPI_WITHHELD_BASIS
      : "income_total − expense_total",
    withheld: netOperatingHeld,
  });

  const netIncome = np?.net ?? null;
  const netIncomeHeld = netKpiWithheld(np, netIncome);
  out.push({
    label: "Net income (period)",
    value: netIncomeHeld
      ? NET_KPI_WITHHELD_VALUE
      : netIncome != null
        ? fmtUsd(num(netIncome))
        : "n/a",
    formula: netIncomeHeld
      ? NET_KPI_WITHHELD_BASIS
      : "QBO net (income − expenses) when reported",
    withheld: netIncomeHeld,
  });

  // Own-source vs reimbursement ratio. We need the income split; if only the
  // summary income_total is known we can't split it, so derive from timeseries
  // trailing 12mo when present.
  let ownRatio: number | null = null;
  let ownSum = 0;
  let pwcSum = 0;
  if (ts?.available && ts.months.length > 0) {
    const recent = ts.months.slice(-12);
    ownSum = recent.reduce((s, m) => s + num(m.own_income), 0);
    pwcSum = recent.reduce((s, m) => s + num(m.pwc_income), 0);
    const tot = ownSum + pwcSum;
    if (tot > 0) ownRatio = ownSum / tot;
  }
  out.push({
    label: "Own-source share of income (TTM)",
    value: ownRatio != null ? `${(ownRatio * 100).toFixed(1)}%` : "n/a",
    formula: "own_income ÷ (own_income + pwc_income), trailing 12 mo",
  });
  out.push({
    label: "PWC reimbursement share (TTM)",
    value: ownRatio != null ? `${((1 - ownRatio) * 100).toFixed(1)}%` : "n/a",
    formula: "pwc_income ÷ total income, trailing 12 mo",
  });

  return out;
}

function buildQboMetrics(
  wb: Workbook,
  ws: Worksheet,
  imageId: number | null,
  data: FinancePackageData,
) {
  const LAST = 3;
  ws.columns = [{ width: 38 }, { width: 18 }, { width: 56 }];
  let r = brandHeader(ws, imageId, "QBO Financial Metrics", data, LAST);

  sectionTitle(ws, r, "Derived BOD metrics (501(c)(4) operating book)");
  r += 1;
  ws.mergeCells(r, 1, r, LAST);
  ws.getCell(r, 1).value =
    "Performance metrics derived from the QBO Balance Sheet + P&L. These are " +
    "operating-book (non-profit) metrics — SEPARATE from the County levy. " +
    "Where an input (e.g. current liabilities) isn't exposed, the metric shows n/a.";
  ws.getCell(r, 1).font = font({
    size: 9,
    italic: true,
    color: { argb: "FF666666" },
  });
  ws.getCell(r, 1).alignment = { wrapText: true, vertical: "middle" };
  r += 2;

  ws.getCell(r, 1).value = "Metric";
  ws.getCell(r, 2).value = "Value";
  ws.getCell(r, 3).value = "Formula / basis";
  styleHeaderRow(ws, r, LAST);
  r += 1;

  const metrics = computeQboMetrics(data.nonprofit, data.npTimeseries);
  metrics.forEach((m, i) => {
    ws.getCell(r, 1).value = m.label;
    const v = ws.getCell(r, 2);
    v.value = m.value;
    v.alignment = { horizontal: "right", vertical: "middle" };
    if (m.withheld) v.font = font({ bold: true, color: { argb: "FF7A5800" } });
    else if (m.value === "n/a")
      v.font = font({ italic: true, color: { argb: "FF999999" } });
    ws.getCell(r, 3).value = m.formula;
    ws.getCell(r, 3).font = font({
      size: 9,
      bold: m.withheld === true,
      color: { argb: m.withheld ? "FF7A5800" : "FF666666" },
    });
    ws.getCell(r, 3).alignment = { wrapText: true, vertical: "middle" };
    styleDataRow(ws, r, LAST, i % 2 === 1);
    r += 1;
  });

  // A withheld figure is NEVER allowed to appear without its reason. The Board
  // must understand the number is held pending explanation — not that the
  // department netted zero.
  const anyWithheld = metrics.some((m) => m.withheld);
  if (anyWithheld) {
    r += 1;
    calloutNote(ws, r, LAST, NET_KPI_HOLD_NOTE);
    ws.getRow(r).height = 132;
    r += 2;
  }

  // Expense composition (top categories as % of total)
  r += 1;
  sectionTitle(ws, r, "Expense composition (top categories)");
  r += 1;
  ["Category", "Amount", "% of total"].forEach(
    (c, i) => (ws.getCell(r, i + 1).value = c),
  );
  styleHeaderRow(ws, r, LAST);
  r += 1;
  const exp = data.npExpenses;
  if (!exp || !exp.available || exp.rows.length === 0) {
    syncingNote(ws, r, "Expense composition");
    r += 1;
  } else {
    const total =
      num(exp.total) || exp.rows.reduce((s, x) => s + num(x.amount), 0);
    exp.rows.slice(0, 10).forEach((row, i) => {
      ws.getCell(r, 1).value = row.category;
      const a = ws.getCell(r, 2);
      a.value = num(row.amount);
      a.numFmt = USD_FMT;
      a.alignment = { horizontal: "right", vertical: "middle" };
      const p = ws.getCell(r, 3);
      p.value = total > 0 ? num(row.amount) / total : 0;
      p.numFmt = PCT_FMT;
      p.alignment = { horizontal: "right", vertical: "middle" };
      styleDataRow(ws, r, LAST, i % 2 === 1);
      r += 1;
    });
  }

  // Monthly income/expense/net trend (24 months)
  r += 1;
  sectionTitle(ws, r, "Monthly income / expense / net trend");
  r += 1;
  // The monthly series is shown as-reported (same as the screen), so the month
  // holding the 55601 contra-credit spikes. Qualify it in-place rather than
  // dropping the series — the Board needs the trend, not a hole.
  if (anyWithheld) {
    calloutNote(ws, r, 5, NET_KPI_TREND_NOTE);
    ws.getRow(r).height = 60;
    r += 2;
  }
  const ts = data.npTimeseries;
  // header (re-using A..C but trend needs 5 cols → extend widths inline)
  const trendCols = ["Month", "Income", "Expense", "Net", "Own / PWC"];
  // widen sheet for trend block (exceljs lazily creates columns on access)
  ws.getColumn(3).width = 18;
  ws.getColumn(4).width = 14;
  ws.getColumn(5).width = 22;
  trendCols.forEach((c, i) => (ws.getCell(r, i + 1).value = c));
  styleHeaderRow(ws, r, 5);
  r += 1;
  if (!ts || !ts.available || ts.months.length === 0) {
    syncingNote(ws, r, "The monthly trend");
  } else {
    const recent = ts.months.slice(-24);
    recent.forEach((m, i) => {
      ws.getCell(r, 1).value = m.label;
      const inc = ws.getCell(r, 2);
      inc.value = num(m.income);
      inc.numFmt = USD_FMT;
      inc.alignment = { horizontal: "right", vertical: "middle" };
      const ex = ws.getCell(r, 3);
      ex.value = num(m.expense);
      ex.numFmt = USD_FMT;
      ex.alignment = { horizontal: "right", vertical: "middle" };
      const net = ws.getCell(r, 4);
      net.value = num(m.net);
      net.numFmt = USD_FMT;
      net.alignment = { horizontal: "right", vertical: "middle" };
      ws.getCell(r, 5).value =
        `${fmtCompact(num(m.own_income))} / ${fmtCompact(num(m.pwc_income))}`;
      ws.getCell(r, 5).font = font({ size: 9, color: { argb: "FF666666" } });
      styleDataRow(ws, r, 5, i % 2 === 1);
      r += 1;
    });

    // ── chart: 24-month income vs expense (line) ──
    r += 1;
    const buf = renderLineChart(
      "QBO income vs expense — trailing 24 months",
      recent.map((m) => m.label),
      [
        {
          label: "Income",
          color: CHART_GREEN_MID,
          values: recent.map((m) => num(m.income)),
        },
        {
          label: "Expense",
          color: CHART_AMBER,
          values: recent.map((m) => num(m.expense)),
        },
        {
          label: "Net",
          color: CHART_GREEN,
          values: recent.map((m) => num(m.net)),
        },
      ],
    );
    r = embedChart(wb, ws, buf, r, { width: 620, height: 248 });
  }

  ws.views = [{ state: "frozen", ySplit: 3 }];
}

function fmtCompact(n: number): string {
  const a = Math.abs(n);
  if (a >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

// ── FA.PL.001 compliance sheet ────────────────────────────────────────────────
// Surfaces the Financial Management Policy's MONTHLY-REVIEW obligations as a
// board checklist — the same items the Board Review page references — with the
// monthly review cadence as the centerpiece. Status is derived from loaded data
// where a source exists; otherwise "Confirm (verbal)" so nothing is fabricated.
function buildCompliance(
  ws: Worksheet,
  imageId: number | null,
  data: FinancePackageData,
) {
  const LAST = 3;
  ws.columns = [{ width: 56 }, { width: 22 }, { width: 46 }];
  let r = brandHeader(ws, imageId, "FA.PL.001 Compliance", data, LAST);

  sectionTitle(ws, r, "Financial Policy (FA.PL.001) — monthly review duties");
  r += 1;

  // Centerpiece callout: the monthly review cadence the Board MUST meet.
  calloutNote(
    ws,
    r,
    LAST,
    "MONTHLY REVIEW CADENCE — FA.PL.001 requires the Board to review the financials EVERY MONTH: " +
      "monthly financials reviewed (§12.1), bank reconciliations current and independently reviewed (§10), " +
      "purchase approvals within the §5.5 thresholds, and budget-vs-actual reviewed. This sheet is the " +
      "standing monthly checklist — work it at each meeting.",
  );
  ws.getRow(r).height = 56;
  r += 2;

  ["Monthly review requirement", "Status", "Basis / how to confirm"].forEach(
    (c, i) => (ws.getCell(r, i + 1).value = c),
  );
  styleHeaderRow(ws, r, LAST);
  r += 1;

  const qboConnected = !!data.nonprofit?.connected;
  // Committee approval progress MUST come from the live v2 committee set
  // (dash_fy27_v2_committee, via fy27V2) — dash_fy27_committee (the v1
  // per-committee approval workflow, read via fy27Build) was abandoned at
  // the 2026-07-06 v2 restructure and its status field never advances, so it
  // always reads "0/8 — Action needed" regardless of the real v2 approval
  // progress. Real committee cards only (exclude the synthetic "Unassigned"
  // bucket, which has no approval workflow of its own).
  const v2Committees = (data.fy27V2?.committees ?? []).filter(
    (c) => !c.is_unassigned,
  );
  const approved =
    v2Committees.length > 0
      ? v2Committees.filter((c) => c.status === "approved").length
      : null;
  const totalC = v2Committees.length > 0 ? v2Committees.length : null;
  const fy27OnTrack =
    approved != null && totalC != null && totalC > 0
      ? approved >= totalC
      : null;

  // status: true = met, false = action needed, null = confirm verbally
  type Status = boolean | null;
  const items: [string, Status, string][] = [
    [
      "Monthly financials reviewed (§12.1)",
      null,
      "Board confirms the monthly financials were presented and reviewed.",
    ],
    [
      "Bank reconciliations current & independently reviewed (§10)",
      qboConnected ? null : false,
      qboConnected
        ? "Confirm in QuickBooks that all accounts are reconciled through last month."
        : "Pending QuickBooks connection — reconciliations not yet auto-verifiable.",
    ],
    [
      "Purchase approvals within FA.PL.001 §5.5 thresholds",
      null,
      "Board confirms approvals were within each band's threshold (see Approvals).",
    ],
    [
      "Budget vs actual reviewed",
      true,
      "Levy Position + by-object tables in this package satisfy the monthly budget-vs-actual review.",
    ],
    [
      `${data.nextFy} budget — formal Board adoption`,
      fy27OnTrack,
      approved != null && totalC != null
        ? `v2 revision built 06-JUL; formal adoption pending — on the next meeting agenda (see ${data.nextFy} Budget tab).`
        : "FY27 build not loaded.",
    ],
    [
      "Unbudgeted board-approved items tied to a motion",
      null,
      "Board confirms any unbudgeted approved item ties back to a recorded motion.",
    ],
  ];

  items.forEach(([label, status, basis], i) => {
    ws.getCell(r, 1).value = label;
    ws.getCell(r, 1).alignment = { wrapText: true, vertical: "middle" };
    const s = ws.getCell(r, 2);
    s.value =
      status === true
        ? "✓ Met"
        : status === false
          ? "Action needed"
          : "Confirm (verbal)";
    s.alignment = { horizontal: "center", vertical: "middle" };
    s.font = font({
      bold: true,
      color: {
        argb:
          status === true
            ? "FF2A7A6C"
            : status === false
              ? "FFB23A2E"
              : "FF7A5800",
      },
    });
    ws.getCell(r, 3).value = basis;
    ws.getCell(r, 3).font = font({ size: 9, color: { argb: "FF666666" } });
    ws.getCell(r, 3).alignment = { wrapText: true, vertical: "middle" };
    styleDataRow(ws, r, LAST, i % 2 === 1);
    ws.getRow(r).height = 46;
    r += 1;
  });

  r += 1;
  ws.mergeCells(r, 1, r, LAST);
  ws.getCell(r, 1).value =
    'A "Confirm (verbal)" status means there is no automated data source yet — the Board confirms it at the ' +
    "meeting; it wires up as QuickBooks and the reconciliation feed come online. No item here fabricates a value.";
  ws.getCell(r, 1).font = font({
    size: 9,
    italic: true,
    color: { argb: "FF666666" },
  });
  ws.getCell(r, 1).alignment = { wrapText: true, vertical: "middle" };
  ws.getRow(r).height = 46;

  ws.views = [{ state: "frozen", ySplit: 3 }];
}

// ── the cover read-me table (kept in sync with the tabs built below) ──────────
function tabDocs(data: FinancePackageData): TabDoc[] {
  return [
    {
      tab: "Levy Position",
      contains:
        "The County levy (fund " +
        fundForFy(data.curFy) +
        ") budgetary-control truth: the 3-lens Funds-Available reconciliation and the by-object budget/committed/obligated/actual table.",
      howToRead:
        'Quote "Funds Available (budgetary control)" — the bottom lens — as spendable. "Budget Remaining (gross)" is informational and overstates it.',
    },
    {
      tab: "Open POs",
      contains:
        "The open-encumbrance register: every open purchase order with supplier, object, remaining, carryover and status.",
      howToRead:
        "These are commitments to vendors, not yet paid. Run them down before fiscal close; carryover rows came from a prior FY.",
    },
    {
      tab: `${data.nextFy} Budget`,
      contains:
        "The next-FY recurring operating budget by committee and supplier/OCA line, with FY24/25/26 actuals, the proposed ask, and %-of-committee.",
      howToRead:
        "Capital + one-time lines are excluded from the recurring total. Each committee subtotal rolls up to the recurring grand total.",
    },
    {
      tab: `${data.nextFy} Initiatives`,
      contains:
        "The 7 County initiatives (27.001–27.007) with amount, current decision, and funding source.",
      howToRead:
        "Decision = the project's review status. Funding source tells you whether it hits the operating levy or is funded outside it.",
    },
    {
      tab: "Non-Profit (QBO)",
      contains:
        "The DCVFD 501(c)(4) operating book from QuickBooks/Truist: cash (restricted flagged), income by source (own vs PWC), and expenses by category.",
      howToRead:
        "This is SEPARATE from the County levy. PWC reimbursement is a pass-through, not own revenue. Cash Available is net of the payroll liability (cash owed to employees); the department's other fund balances are unrestricted and count as available.",
    },
    {
      tab: "QBO Metrics",
      contains:
        "Derived BOD performance metrics from the QBO Balance Sheet + P&L: ratios, runway, net operating/income, own-vs-reimbursement, expense mix, and a 24-month income-vs-expense chart.",
      howToRead:
        "Operating-book metrics only (not the levy). A metric shows 'n/a' when an input isn't available and 'Withheld' when finance is holding the figure pending explanation — 'Withheld' means UNKNOWN, not zero, and always carries a written reason. Nothing is fabricated.",
    },
    {
      tab: "FA.PL.001 Compliance",
      contains:
        "The Financial Policy (FA.PL.001) monthly-review checklist: monthly financials, reconciliations, approval thresholds, and budget-vs-actual — with status.",
      howToRead:
        "Work this list at each monthly meeting. 'Confirm (verbal)' = no automated source yet; the Board confirms it live.",
    },
  ];
}

// ── logo fetch ────────────────────────────────────────────────────────────────
async function fetchLogo(): Promise<ArrayBuffer | null> {
  if (typeof fetch === "undefined") return null;
  try {
    const res = await fetch(LOGO_PATH);
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

// ── public entry: build + download ────────────────────────────────────────────

/** Build the branded multi-sheet workbook and return it as a Blob (no download).
 *  Exported separately so tests / callers can inspect the bytes. */
export async function buildFinancePackageBlob(
  data: FinancePackageData,
  opts?: { logo?: ArrayBuffer | null },
): Promise<Blob> {
  const ExcelJS = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  wb.creator = "DCVFD Ember Finance Dashboard";
  wb.created = new Date();
  wb.title = "DCVFD Finance Package";

  // Embed the logo once at the workbook level; reuse the id on every sheet.
  // Callers without a DOM / relative fetch (the Worker weekly-email job) pass the
  // logo bytes via opts.logo; the browser falls back to fetchLogo().
  let imageId: number | null = null;
  const logo = opts && "logo" in opts ? opts.logo : await fetchLogo();
  if (logo) {
    // exceljs's addImage buffer type is its own ExcelJS.Buffer (a Node Buffer
    // alias); an ArrayBuffer is accepted at runtime in the browser bundle.
    imageId = wb.addImage({
      buffer: logo as unknown as Parameters<typeof wb.addImage>[0]["buffer"],
      extension: "png",
    });
  }

  const tabs = tabDocs(data);

  // Each sheet builds in isolation: a thrown error in one builder is caught and
  // written into that sheet as a note, so a single bad sheet never crashes the
  // whole export. (Resilience rule.)
  const safe = (sheetName: string, build: (ws: Worksheet) => void) => {
    const ws = wb.addWorksheet(sheetName);
    try {
      build(ws);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      ws.getCell("A1").value = `This sheet could not be built: ${msg}`;
      ws.getCell("A1").font = font({
        italic: true,
        color: { argb: "FF999999" },
      });
    }
  };

  safe("Cover", (ws) => buildCover(ws, imageId, data, tabs));
  safe("Levy Position", (ws) => buildLevyPosition(wb, ws, imageId, data));
  safe("Open POs", (ws) => buildOpenPos(ws, imageId, data));
  // v2 IS the FY27 Budget now (the old v1 supplier-level sheet was retired).
  safe(`${data.nextFy} Budget`, (ws) => buildFy27V2(wb, ws, imageId, data));
  // Bridge sheet ELIMINATED per RJ 6/15 — the abstract base→operating-need
  // walk confused the board. The County-approved operating adds + carryover are
  // folded into the FY27 Budget conversation instead.
  safe(`${data.nextFy} Initiatives`, (ws) =>
    buildInitiatives(ws, imageId, data),
  );
  safe("Non-Profit (QBO)", (ws) => buildNonProfit(ws, imageId, data));
  safe("QBO Metrics", (ws) => buildQboMetrics(wb, ws, imageId, data));
  safe("FA.PL.001 Compliance", (ws) => buildCompliance(ws, imageId, data));

  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/** Build the workbook and trigger a browser download. Filename is stamped with
 *  the as-of date. No-op outside a browser. */
export async function downloadFinancePackage(
  data: FinancePackageData,
): Promise<void> {
  const blob = await buildFinancePackageBlob(data);
  if (typeof document === "undefined" || typeof URL === "undefined") return;
  const stamp = (data.asOf || new Date().toISOString().slice(0, 10)).replace(
    /[^0-9-]/g,
    "",
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `dcvfd-finance-package-${stamp}.xlsx`;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
